import window from 'global/window';
import logger from './logger';
import { getEnvironmentConstant, getLocaleFromPath } from './utilities';
import {
  ADP_CJS_CLIENT_ID,
  BASENAME,
  DEV_ENV,
  PROD_ENV,
  QA_ENV,
} from './constants';

// Registered with the CJS SDK team. Every event emitted by this property carries it.
export const EVENT_GENERATOR = 'partner-finder';

export const PAGE_VIEW_EVENT = 'page_view';

// Emitted by conversion-web-analytics-utils once window.CjsTracker is available.
export const CJS_READY_EVENT = 'cjsTracker:ready';

// The SDK scripts are deferred, so they may still be in flight when the bundle hydrates.
const READY_TIMEOUT_MS = 10000;

const VALID_SDK_ENVS = ['dev', 'stg', 'prd'];
const DEFAULT_SDK_ENV = 'stg';

export const PAGE_CATEGORY = {
  INDEX: 'partner-finder',
  PROFILE: 'partner-profile',
  SUCCESS_STORY: 'partner-success-story',
  NOT_FOUND: 'error',
};

const SUCCESS_STORIES_SEGMENT = 'success-stories';

let initPromise = null;

const getSdkInfo = () => window?.adsk?.adpSdkInfo || {};

/**
 * Path segments that follow the /support/partners basename, with any locale prefix removed.
 * Returns null when the current path is not a Partner Finder route.
 */
const getRouteSegments = (pathname) => {
  const basenameIndex = pathname.indexOf(BASENAME);
  if (basenameIndex === -1) {
    return null;
  }
  return pathname
    .slice(basenameIndex + BASENAME.length)
    .split('/')
    .filter(Boolean);
};

/**
 * Maps the current route to the page_category reported to CJS. Mirrors the route table in
 * routerConfig.js: SRP, partner profile, partner success story, and the catch-all 404.
 */
export const getPageCategory = (pathname = window?.location?.pathname || '') => {
  const segments = getRouteSegments(pathname);
  if (segments === null) {
    return PAGE_CATEGORY.NOT_FOUND;
  }
  if (segments.length === 0) {
    return PAGE_CATEGORY.INDEX;
  }
  if (segments[0] === SUCCESS_STORIES_SEGMENT) {
    return segments.length === 3 ? PAGE_CATEGORY.SUCCESS_STORY : PAGE_CATEGORY.NOT_FOUND;
  }
  return segments.length === 2 ? PAGE_CATEGORY.PROFILE : PAGE_CATEGORY.NOT_FOUND;
};

/**
 * Resolves the locale in the underscore form CJS expects (en_US rather than en-US).
 */
export const getPageLocale = (
  pathname = window?.location?.pathname || '',
  hostname = window?.location?.hostname || '',
) => {
  // Routes are /:locale?/support/partners, so a locale prefix is the segment before the basename.
  const basenameIndex = pathname.indexOf(BASENAME);
  const prefix = basenameIndex === -1 ? '' : pathname.slice(0, basenameIndex);
  const localeKey = prefix.split('/').filter(Boolean)[0];
  return getLocaleFromPath(localeKey, hostname, {}).replace('-', '_');
};

const getPageData = () => ({
  page_category: getPageCategory(),
  page_locale: getPageLocale(),
});

/**
 * Resolves the ADP Web SDK client ID. Prefers environment constants (supports dev/qa/stg/prd
 * when index-stg.html is shared) and falls back to window.adsk.adpSdkInfo.clientId from HTML.
 */
export const getAdpCjsClientId = (environment) => {
  if (environment) {
    const configuredId = getEnvironmentConstant({ env: environment, key: ADP_CJS_CLIENT_ID });
    if (configuredId) {
      return configuredId;
    }
  }
  return getSdkInfo().clientId || '';
};

/**
 * Maps Partner Finder runtime environment to ADP SDK env (mirrors dotcom AdpConversion.js).
 */
export const getAdpCjsSdkEnv = (environment) => {
  if (environment === PROD_ENV) {
    return 'prd';
  }
  if (environment === QA_ENV || environment === DEV_ENV) {
    return 'dev';
  }
  const { env } = getSdkInfo();
  return VALID_SDK_ENVS.includes(env) ? env : DEFAULT_SDK_ENV;
};

/**
 * Resolves once window.CjsTracker is usable, or false if it never turns up. The tracker may
 * already be loaded by the time we get here, in which case the ready event has come and gone.
 */
const waitForCjsTracker = () => new Promise((resolve) => {
  if (typeof window.CjsTracker === 'function') {
    resolve(true);
    return;
  }

  let timeoutId = null;

  const onReady = () => {
    clearTimeout(timeoutId);
    resolve(typeof window.CjsTracker === 'function');
  };

  timeoutId = setTimeout(() => {
    window.removeEventListener(CJS_READY_EVENT, onReady);
    resolve(typeof window.CjsTracker === 'function');
  }, READY_TIMEOUT_MS);

  window.addEventListener(CJS_READY_EVENT, onReady, { once: true });
});

const runInit = async (environment) => {
  const clientId = getAdpCjsClientId(environment);
  if (!clientId) {
    logger.debug('[CJS] No ADP client ID configured. Skipping CJS initialization.');
    return false;
  }

  const ready = await waitForCjsTracker();
  if (!ready) {
    logger.warn('[CJS] CjsTracker was not available. Check that the conversion-web-analytics script loaded.');
    return false;
  }

  if (typeof window.Adp !== 'function') {
    logger.warn('[CJS] Adp() is not available. Check that the ADP Web SDK script loaded.');
    return false;
  }

  try {
    await window.CjsTracker.init(window.Adp())
      .setEnv(getAdpCjsSdkEnv(environment))
      // Auto-captures link_click, including the data-wat-* attributes already on our components.
      .trackClickElements()
      .defaultEventAttributes({ event_generator: EVENT_GENERATOR })
      .defaultEventData({ page: getPageData() })
      .load({ id: clientId });
    logger.debug('[CJS] Tracker initialized.');
    return true;
  } catch (error) {
    logger.error('[CJS] Error initializing SDK: ', error);
    return false;
  }
};

/**
 * Initializes the CJS tracker. Safe to call more than once; only the first call does work.
 * Never throws: analytics failures must not break the page.
 */
export const initCjs = (environment) => {
  if (initPromise === null) {
    initPromise = runInit(environment).catch((error) => {
      logger.error('[CJS] Unexpected error during initialization: ', error);
      return false;
    });
  }
  return initPromise;
};

/**
 * Emits a page_view for client-side navigation. The SDK auto-captures the first page view on
 * load(), so callers must skip the initial render to avoid double counting.
 */
export const trackPageView = () => {
  const cjs = window?.adsk?.cjs;
  if (!cjs || typeof cjs.track !== 'function') {
    return;
  }
  try {
    cjs.track(PAGE_VIEW_EVENT, EVENT_GENERATOR, { page: getPageData() });
  } catch (error) {
    logger.error('[CJS] Error tracking page view: ', error);
  }
};
